import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../lib/auth';

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('Ugyldig tilbakestillingslenke');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passordene matcher ikke');
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError('Passordet må være minst 8 tegn');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, newPassword);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke tilbakestille passord');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Passord tilbakestilt!
            </h2>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-sm text-green-800">
              Passordet ditt har blitt tilbakestilt.
            </p>
            <p className="text-sm text-green-800 mt-2">
              Du blir automatisk sendt til innloggingssiden...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Ugyldig lenke
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Tilbakestillingslenken er ugyldig eller utløpt.
            </p>
          </div>
          <div className="text-center">
            <a
              href="/forgot-password"
              className="text-sm text-kf-primary hover:text-kf-secondary"
            >
              Be om ny tilbakestillingslenke
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Opprett nytt passord
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Skriv inn ditt nye passord
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="label">
                Nytt passord
              </label>
              <input
                id="newPassword"
                type="password"
                required
                minLength={8}
                className="input"
                placeholder="Minimum 8 tegn"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="label">
                Bekreft passord
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                minLength={8}
                className="input"
                placeholder="Gjenta passord"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full"
          >
            {loading ? 'Tilbakestiller...' : 'Tilbakestill passord'}
          </button>
        </form>
      </div>
    </div>
  );
}
